import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useProviderLoader } from '@/Providers/useProviderLoader'
import { ContainerPage } from '@/components/common'
import { SubTitle } from '@/components/common/UI'
import { InputControl } from '@/components/form-controls'
import { Button, Card, CardBody, CardHeader, Image } from '@/components/nextui'
import { useAuthApi } from '@/services/api/authApi'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'
import { inputValidations } from '@/utils/inputValidations'

const COOLDOWN_SECONDS = 60

type ApiErrorBody = {
    response?: { data?: { message?: Array<{ messageDesc?: string }> } }
}

type ResetResponse = { status: number; message?: Array<{ messageDesc?: string }> }

function getErrorFromResponse(response: ResetResponse, fallback: string): string | null {
    if (response.status === 200) {
        return null
    }
    return response.message?.[0]?.messageDesc ?? fallback
}

function getErrorFromCaught(error: unknown, fallback: string): string {
    const err = error as ApiErrorBody
    return err.response?.data?.message?.[0]?.messageDesc ?? fallback
}

function useCooldown() {
    const [cooldownSeconds, setCooldownSeconds] = useState(0)
    useEffect(() => {
        if (cooldownSeconds <= 0) {
            return
        }
        const id = setInterval(() => setCooldownSeconds((s) => (s <= 1 ? 0 : s - 1)), 1000)
        return () => clearInterval(id)
    }, [cooldownSeconds])
    const startCooldown = () => setCooldownSeconds(COOLDOWN_SECONDS)
    return { cooldownSeconds, startCooldown }
}

function usePasswordResetActions() {
    const { t } = useTranslation()
    const { requestPasswordReset } = useAuthApi({
        errorHandling: ErrorHandlingStrategy.RETURN,
        messageLevel: MessageLevel.ALL
    })
    const { showLoader, hideLoader } = useProviderLoader()
    const { cooldownSeconds, startCooldown } = useCooldown()

    const [successMessage, setSuccessMessage] = useState<{
        title: string
        description: string
    } | null>(null)
    const [email, setEmail] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const [isResending, setIsResending] = useState(false)
    const [resendError, setResendError] = useState<string | null>(null)

    const onSubmit: SubmitHandler<{ email: string }> = async (data) => {
        setErrorMessage('')
        showLoader()
        try {
            const response = await requestPasswordReset(data.email)
            const errMsg = getErrorFromResponse(response, t('passwordReset.error'))
            if (errMsg === null) {
                setEmail(data.email)
                setSuccessMessage({
                    title: t('passwordReset.reviewYourEmailTitle'),
                    description: t('passwordReset.reviewYourEmailDescription')
                })
                startCooldown()
            } else {
                setErrorMessage(errMsg)
            }
        } catch (error: unknown) {
            setErrorMessage(getErrorFromCaught(error, t('passwordReset.error')))
            console.error('Password reset request error:', error)
        } finally {
            hideLoader()
        }
    }

    const handleResendPasswordChange = async () => {
        if (!email || isResending || cooldownSeconds > 0) {
            return
        }
        setResendError(null)
        setIsResending(true)
        showLoader()
        try {
            const response = await requestPasswordReset(email)
            const errMsg = getErrorFromResponse(response, t('passwordReset.resendError'))
            if (errMsg === null) {
                setResendError(null)
                startCooldown()
            } else {
                setResendError(errMsg)
            }
        } catch (error: unknown) {
            setResendError(getErrorFromCaught(error, t('passwordReset.resendError')))
        } finally {
            setIsResending(false)
            hideLoader()
        }
    }

    return {
        onSubmit,
        handleResendPasswordChange,
        successMessage,
        email,
        errorMessage,
        isResending,
        resendError,
        cooldownSeconds
    }
}

export const PasswordReset = (): JSX.Element => {
    const { t } = useTranslation()
    const { control, handleSubmit } = useForm<{ email: string }>()
    const actions = usePasswordResetActions()

    return (
        <ContainerPage>
            <article className="p-4 bg-gradient-ean-2 dark:bg-gradient-ean">
                <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 2, ease: 'easeOut' }}
                    className="flex flex-col justify-center items-center"
                >
                    <Card className="w-full backdrop-blur-md lg:w-4/5 lg:mx-auto lg:my-0 bg-white/90 dark:bg-gray-800/90">
                        <CardHeader className="flex flex-col gap-4 justify-center items-center">
                            <Image src="/LogoEAN.jpg" alt="logo" className="w-[150px]" />
                            <SubTitle
                                className="font-normal text-center"
                                subTitle={
                                    actions.successMessage ? actions.successMessage.title : t('passwordReset.title')
                                }
                            />
                            <p className="text-center text-sm font-light text-disabled">
                                {actions.successMessage ? actions.successMessage.description : t('passwordReset.success')}
                            </p>
                            {actions.successMessage && (
                                <p className="text-center text-sm font-light text-success">
                                    {t('passwordReset.emailSentTo', { email: actions.email })}
                                </p>
                            )}
                        </CardHeader>
                        <CardBody className="flex flex-col gap-4">
                            {actions.errorMessage && !actions.successMessage && (
                                <p className="text-center text-sm text-danger">{actions.errorMessage}</p>
                            )}
                            {!actions.successMessage && (
                                <form
                                    onSubmit={handleSubmit(actions.onSubmit)}
                                    className="space-y-4 lg:mx-auto lg:my-0 lg:w-4/5"
                                >
                                    <InputControl
                                        allowedPattern={inputValidations.emailPattern}
                                        preserveCase
                                        name="email"
                                        control={control}
                                        label={t('forms.common.email')}
                                        rules={{
                                            required: t('forms.common.required.emailRequired'),
                                            pattern: {
                                                value: inputValidations.isValidEmail,
                                                message: t('createAccount.invalidEmail')
                                            }
                                        }}
                                        className="text-gray-800"
                                    />
                                    <div className="flex justify-evenly items-center">
                                        <Button type="submit" color="primary" className="w-full">
                                            {t('passwordReset.submit')}
                                        </Button>
                                    </div>
                                </form>
                            )}
                            {actions.successMessage && (
                                <>
                                    {actions.resendError && (
                                        <p className="text-center text-sm text-danger">{actions.resendError}</p>
                                    )}
                                    <Button
                                        color="default"
                                        variant="bordered"
                                        className="w-full"
                                        onPress={actions.handleResendPasswordChange}
                                        isDisabled={
                                            actions.isResending ||
                                            actions.cooldownSeconds > 0
                                        }
                                    >
                                        {actions.cooldownSeconds > 0
                                            ? t('passwordReset.resendCooldown', { seconds: actions.cooldownSeconds })
                                            : t('passwordReset.resendPasswordChange')}
                                    </Button>
                                </>
                            )}
                            <Link
                                to="/login"
                                className="text-xs font-light text-center underline text-disabled"
                            >
                                {t('passwordReset.backToLogin')}
                            </Link>
                        </CardBody>
                    </Card>
                </motion.div>
            </article>
        </ContainerPage>
    )
}
